export const H3_BREAKDOWN_RESOLUTIONS = [2, 3, 4, 5, 6, 7] as const;

export type H3BreakdownResolution = (typeof H3_BREAKDOWN_RESOLUTIONS)[number];

// Average H3 hexagon areas in square kilometers, matching h3-js averageHexagonArea values.
const H3_AVERAGE_AREA_KM2: Record<H3BreakdownResolution, number> = {
  2: 86801.780398997,
  3: 12393.434655088,
  4: 1770.347654491,
  5: 252.903858182,
  6: 36.129062164,
  7: 5.16129336,
};

// Matches the km²->mi² conversion used elsewhere in the app (e.g. lat-lng-utils.ts).
const SQ_MI_PER_SQ_KM = 0.386102;

const H3_FILTER_FIELD_REGEX = /^h3_([2-7])_ix$/;

const formatAreaNumber = (areaMi2: number): string => {
  const maximumFractionDigits = areaMi2 >= 100 ? 0 : areaMi2 >= 10 ? 1 : 2;

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits,
  }).format(areaMi2);
};

export const getH3ResolutionFromFilterField = (
  field: string,
): H3BreakdownResolution | null => {
  const match = field.match(H3_FILTER_FIELD_REGEX);
  if (!match) return null;

  const resolution = Number(match[1]);
  return H3_BREAKDOWN_RESOLUTIONS.includes(resolution as H3BreakdownResolution)
    ? (resolution as H3BreakdownResolution)
    : null;
};

export const isH3IndexFilterField = (field: string): boolean =>
  getH3ResolutionFromFilterField(field) !== null;

const getH3ResolutionAreaMi2 = (
  resolution: H3BreakdownResolution | number,
): number | null => {
  if (!H3_BREAKDOWN_RESOLUTIONS.includes(resolution as H3BreakdownResolution)) {
    return null;
  }

  return H3_AVERAGE_AREA_KM2[resolution as H3BreakdownResolution] * SQ_MI_PER_SQ_KM;
};

export const getH3ResolutionAreaLabel = (
  resolution: H3BreakdownResolution | number,
): string => {
  const areaMi2 = getH3ResolutionAreaMi2(resolution);
  if (areaMi2 === null) return "Unknown area";

  return `${formatAreaNumber(areaMi2)} mi²`;
};
